// Deep-path import: the common barrel eagerly requires server-only deps
// (nats, jsonwebtoken) that webpack cannot bundle for the browser.
import { deriveDwellingClassification } from '@alpinehomeair/common/build/helpers/derive-dwelling-classification';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import Select from 'components/inputs/select';
import { setStructureName, setStructureSubtypeId } from 'features/structure';
import { resolveDwellingSuggestion } from 'utils/dwelling-suggestion';
import { confirmationModal } from 'utils/ui';

type StructureSubypeSelectorProps = {
  showInvalid?: boolean;
  loading?: boolean;
};

export default function StructureSubypeSelector({
  showInvalid,
  loading
}: StructureSubypeSelectorProps) {
  const dispatch = useAppDispatch();
  const { structureTypeId, structureSubtypeId, structureNameUserEdited } =
    useAppSelector((state) => state.structure);
  const { configurations } = useAppSelector((state) => state.configurations);
  const { structure } = useAppSelector((state) => state.structure);
  const { selectedSystems } = useAppSelector((state) => state.systems);
  const { structureTypes } = useAppSelector((state) => state.data);
  const { attomData } = useAppSelector((state) => state.project);

  const dwellingSuggestion = resolveDwellingSuggestion(
    deriveDwellingClassification(attomData?.summary),
    structureTypes
  );

  const handleChangeStructureSubtype = (
    newStructureSubtypeId: number | null
  ) => {
    if (newStructureSubtypeId === structureSubtypeId) {
      return;
    }

    const changeStructureSubtype = () => {
      dispatch(setStructureSubtypeId(newStructureSubtypeId));
      if (!structureNameUserEdited) {
        dispatch(
          setStructureName(
            structureTypes
              .find((t) => t.id === structureTypeId)
              ?.subtypes.find((s) => s.id === newStructureSubtypeId)?.label ??
              'Unnamed Structure'
          )
        );
      }
    };
    if (
      structure.length === 0 &&
      configurations.length === 0 &&
      selectedSystems.length === 0
    ) {
      changeStructureSubtype();
    } else {
      confirmationModal(
        'Are you sure you want to change the structure subtype? This will reset your structure, configurations, and selected systems!',
        changeStructureSubtype
      );
    }
  };

  return loading ? (
    <Select placeholder='Loading...' disabled />
  ) : (
    <>
      <Select
        onChange={(value) =>
          handleChangeStructureSubtype(value !== '' ? Number(value) : null)
        }
        options={
          structureTypes
            .find((t) => t.id === structureTypeId)
            ?.subtypes.map((s) => ({
              label: s.label,
              value: s.id.toString()
            })) || []
        }
        value={structureSubtypeId?.toString() ?? undefined}
        invalid={showInvalid && structureSubtypeId === null}
      />
      {structureSubtypeId === null &&
        dwellingSuggestion &&
        dwellingSuggestion.typeId === structureTypeId && (
          <span className='text-sm text-gray-500'>
            Suggested from property records: {dwellingSuggestion.label}
          </span>
        )}
    </>
  );
}
